<template>
  <v-navigation-drawer v-model="drawerStore.isOpen" app>
    <div class="sidebar-content">
      <v-list class="sidebar-nav">
        <v-list-item
          v-for="(item, index) in menuItems"
          :key="item.title"
          :to="item.path"
          :active="isActive(item.path)"
          @keydown="handleNavigationKeydown"
          @focus="handleItemFocus(index)"
        >
          <template #prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </v-list>
      <div class="sidebar-footer">
        <span class="sidebar-footer-title">{{ $t('app.title') }}</span>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDrawerStore } from '@/stores/drawer';

const route = useRoute();
const { t } = useI18n();
const drawerStore = useDrawerStore();
const currentFocusIndex = ref(0);

const menuItems = computed(() => [
  { title: t('navigation.dashboard'), path: '/', icon: 'mdi-view-dashboard' },
  { title: t('navigation.users'), path: '/users', icon: 'mdi-account-group' },
  {
    title: t('navigation.resources'),
    path: '/resources',
    icon: 'mdi-hospital-building',
  },
  {
    title: t('navigation.appointments'),
    path: '/appointments',
    icon: 'mdi-calendar',
  },
  {
    title: t('navigation.rules'),
    path: '/rules',
    icon: 'mdi-shield-check',
  },
  { title: t('navigation.chat'), path: '/chat', icon: 'mdi-chat' },
  { title: t('navigation.reports'), path: '/reports', icon: 'mdi-chart-bar' },
]);

const isActive = (path: string) => {
  return route.path === path || route.path.startsWith(`${path}/`);
};

const handleNavigationKeydown = (event: KeyboardEvent) => {
  const target = event.currentTarget as HTMLElement;
  const listItem = target.closest('.v-list-item') as HTMLElement;
  const listItems = Array.from(document.querySelectorAll('.v-list-item'));
  const currentIndex = listItems.indexOf(listItem);
  // Simple navigation: up/down arrow keys
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    const nextIndex = currentIndex < menuItems.value.length - 1 ? currentIndex + 1 : 0;
    const targetItem = listItems[nextIndex] as HTMLElement;
    if (targetItem) targetItem.focus();
    currentFocusIndex.value = nextIndex;
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.value.length - 1;
    const targetItem = listItems[prevIndex] as HTMLElement;
    if (targetItem) targetItem.focus();
    currentFocusIndex.value = prevIndex;
  }
};

const handleItemFocus = (index: number) => {
  currentFocusIndex.value = index;
};
</script>

<style scoped>
.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
}

.sidebar-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin-top: auto;
}

.sidebar-footer-title {
  font-weight: 600;
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.87);
}
</style>
